import { Controller } from '@hotwired/stimulus'
import { renderPdf, watchContainerWidth } from '../lib/pdf_renderer'
import { trackScroll, checkIfFitsWithoutScroll, waitForVisible } from '../lib/scroll_tracker'

// Reading stalled for this long and the bottom still not reached : the cue
// comes back rather than leaving the reader without a way forward.
const CUE_IDLE_DELAY = 2200

export default class extends Controller {
  static targets = [
    'container',
    'loading',
    'check',
    'cue',
    'announcement',
  ]
  static values = {
    url: String,
    docId: Number,
    overlays: { type: Object, default: { signatures: [], initials: [] } },
    signatureLabel: { type: String, default: 'Votre signature' },
    initialsLabel: { type: String, default: 'Vos initiales' },
    pageSingular: { type: String, default: 'page' },
    pagePlural: { type: String, default: 'pages' },
    readAnnouncement: String,
  }

  connect() {
    this.scrolled = false
    this.rendering = false
    waitForVisible(this.containerTarget, () => this.loadDocument())
  }

  disconnect() {
    if (this.stopWatching) this.stopWatching()
    clearTimeout(this.cueIdleTimer)
  }

  // Any scroll means the reader is on it — the cue steps aside until they stall.
  noteScroll() {
    this.hideCue()
    clearTimeout(this.cueIdleTimer)
    this.cueIdleTimer = setTimeout(() => this.showCue(), CUE_IDLE_DELAY)
  }

  // Goes all the way down, as the pill's label promises. A one-screen nudge left
  // a long document several clicks away from the bottom, and each nudge read as
  // "the reader took over" — so the pill faded for 2.2s between clicks.
  jumpDown() {
    this.containerTarget.scrollTo({
      top: this.containerTarget.scrollHeight,
      behavior: 'smooth',
    })
  }

  async loadDocument() {
    try {
      const renderedPages = await renderPdf(this.urlValue, this.containerTarget)
      this.drawOverlays(renderedPages)
      this.announcePageCount(renderedPages.length)
      this.hideLoading()
      trackScroll(this.containerTarget, () => this.markAsRead())
      checkIfFitsWithoutScroll(
        this.containerTarget,
        () => this.markAsRead(),
        () => this.showCue()
      )
      this.stopWatching = watchContainerWidth(
        this.containerTarget,
        () => this.rerender()
      )
    } catch {
      this.containerTarget.innerHTML =
        '<p class="text-center text-base-content/60 p-8">Document unavailable</p>'
      this.hideLoading()
    }
  }

  async rerender() {
    if (this.rendering) return
    this.rendering = true
    const scrollTop = this.containerTarget.scrollTop
    try {
      this.containerTarget.innerHTML = ''
      const renderedPages = await renderPdf(this.urlValue, this.containerTarget)
      this.drawOverlays(renderedPages)
      this.containerTarget.scrollTop = scrollTop
    } catch (error) {
      console.error('PDF re-render failed', error)
    } finally {
      this.rendering = false
    }
  }

  drawOverlays(renderedPages) {
    const overlays = this.overlaysValue || {}
    const signatures = Array.isArray(overlays.signatures) ? overlays.signatures : []
    const initials = Array.isArray(overlays.initials) ? overlays.initials : []
    if (signatures.length === 0 && initials.length === 0) return

    const pagesByNumber = new Map(renderedPages.map((p) => [p.page, p]))

    renderedPages.forEach((page) => {
      const wrapper = document.createElement('div')
      wrapper.style.position = 'relative'
      wrapper.style.display = 'block'
      page.canvas.parentNode.insertBefore(wrapper, page.canvas)
      wrapper.appendChild(page.canvas)
      page.wrapper = wrapper
    })

    signatures
      .filter((o) => pagesByNumber.has(o.page))
      .forEach((overlay) => {
        const page = pagesByNumber.get(overlay.page)
        this.appendOverlay(page, overlay, 'signature')
      })

    initials
      .filter((o) => pagesByNumber.has(o.page))
      .forEach((overlay) => {
        const page = pagesByNumber.get(overlay.page)
        this.appendOverlay(page, overlay, 'initials')
      })
  }

  appendOverlay(page, overlay, kind) {
    const { scale, pdfHeight, wrapper } = page
    const leftPx = overlay.x * scale
    const widthPx = overlay.width * scale
    const heightPx = overlay.height * scale
    const topPx = (pdfHeight - overlay.y - overlay.height) * scale

    const box = document.createElement('div')
    box.style.position = 'absolute'
    box.style.left = `${leftPx}px`
    box.style.top = `${topPx}px`
    box.style.width = `${widthPx}px`
    box.style.height = `${heightPx}px`
    box.style.boxSizing = 'border-box'
    box.style.borderRadius = '6px'
    box.style.display = 'flex'
    box.style.alignItems = 'center'
    box.style.justifyContent = 'center'
    box.style.textAlign = 'center'
    box.style.padding = '4px'
    box.style.pointerEvents = 'none'
    box.style.fontFamily = '"General Sans", system-ui, sans-serif'
    box.style.fontWeight = '600'
    box.style.color = '#023836'
    box.style.userSelect = 'none'

    if (kind === 'signature') {
      box.style.border = '2px dashed #023836'
      box.style.background = 'rgba(177, 250, 51, 0.28)'
      box.textContent = this.signatureLabelValue
    } else {
      box.style.border = '1.5px dashed #023836'
      box.style.background = 'rgba(2, 56, 54, 0.10)'
      box.textContent = this.initialsLabelValue
    }

    const fontSize = Math.max(8, Math.min(13, heightPx / 4))
    box.style.fontSize = `${fontSize}px`
    box.style.lineHeight = '1.15'

    page.wrapper.appendChild(box)
  }

  markAsRead() {
    if (this.scrolled) return
    this.scrolled = true

    clearTimeout(this.cueIdleTimer)
    this.hideCue()

    if (this.hasCheckTarget) {
      this.checkTarget.classList.add('sign-doc-read-badge-in')
    }

    // The badge is aria-hidden, so this is the only spoken confirmation that
    // the step is cleared.
    if (this.hasAnnouncementTarget) {
      this.announcementTarget.textContent = this.readAnnouncementValue
    }

    this.dispatch('scrolledToBottom', {
      detail: { docId: this.docIdValue }
    })
  }

  showCue() {
    if (this.scrolled || !this.hasCueTarget) return

    this.cueTarget.classList.remove('sign-scroll-cue-out')
    this.cueTarget.removeAttribute('inert')
    this.cueTarget.removeAttribute('aria-hidden')
  }

  hideCue() {
    // Guarded because this runs on every scroll event, and the DOM writes below
    // would otherwise be repeated dozens of times per second for nothing.
    if (!this.hasCueTarget || this.cueTarget.classList.contains('sign-scroll-cue-out')) return

    this.cueTarget.classList.add('sign-scroll-cue-out')
    this.cueTarget.setAttribute('inert', 'inert')
    this.cueTarget.setAttribute('aria-hidden', 'true')
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  // Only pdf.js knows how long the file is, and the row that says so lives in
  // the header, out of reach — so the count leaves as an event and signing-flow,
  // whose scope covers the whole screen, puts it where it belongs. The wording
  // stays here : this controller is the one holding the translated labels.
  announcePageCount(count) {
    if (!count) return

    const label = count > 1 ? this.pagePluralValue : this.pageSingularValue
    this.dispatch('pagesRendered', {
      detail: { docId: this.docIdValue, label: ` · ${count} ${label}` }
    })
  }
}
